import cartFilters from '@/blade-components/cart-filters.js';
import formSelect from '@/blade-components/form-select.js';
import profileForm from '@/blade-components/profile-form';
import profileInfo from '@/blade-components/profile-info.js';
import quantitySelector from '@/blade-components/quantity-selector';
import { useCartFetchStore } from '@/stores/cartFetchStore.ts';
import collapse from '@alpinejs/collapse';
import mask from '@alpinejs/mask';
import persist from '@alpinejs/persist';
import Alpine from 'alpinejs';
import './bootstrap';

Alpine.plugin(mask);
Alpine.plugin(collapse);
Alpine.plugin(persist);

Alpine.data('quantitySelector', quantitySelector);
Alpine.data('profileInfo', profileInfo);
Alpine.data('profileForm', profileForm);

// Custom Form
Alpine.data('formSelect', formSelect);
Alpine.data('cartFilters', cartFilters);

Alpine.store('cart', {
    count: 0,
    open: false,
    items: [],
    amount: 0,
    initialized: false,
    toggle() {
        this.open = !this.open;
    },
    close() {
        this.open = false;
    },

    get hasOutOfStock() {
        return this.items.some(
            (item) => item.productVariation.inStock === false,
        );
    },

    getItemByVariation(variationId) {
        return this.items.find(
            (item) => item?.productVariation?.id === variationId,
        );
    },

    getItem(id) {
        return this.items.find((item) => item?.id === id);
    },
    async update() {
        const response = await fetch('/cart/fetch');
        if (response.ok) {
            const data = await response.json();
            this.count = data.count;
            this.items = data.items;
            this.amount = data.amount;

            this.initialized = true;

            useCartFetchStore.getState().setCartData({
                items: data.items,
                count: data.count,
                amount: data.amount,
            });
        }
        return this;
    },
    async removeItem(id) {
        const response = await fetch(`/cart/${id}/delete`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document
                    .querySelector('meta[name="csrf-token"]')
                    .getAttribute('content'),
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            window.updateReactCart?.(data.items);
            await useCartFetchStore.getState().fetchCart();
            this.count = data.count;
            this.items = data.items;
            this.amount = data.amount;
        }
    },

    async addItem(id, quantity = 1) {
        const response = await fetch(`/cart/${id}/add?quantity=${quantity}`, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            window.updateReactCart?.(data.items);
            await useCartFetchStore.getState().fetchCart();
            this.count = data.count;
            this.items = data.items;
            this.amount = data.amount;
        }
    },

    async clear() {
        const response = await fetch(`/cart/truncate`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document
                    .querySelector('meta[name="csrf-token"]')
                    .getAttribute('content'),
            },
        });
        if (response.ok) {
            window.updateReactCart?.([]);
            await useCartFetchStore.getState().fetchCart();
            this.count = 0;
            this.items = [];
            this.amount = 0;
        }
    },

    async repeatOrderToCart(orderId, shouldReplace) {
        const response = await fetch(`/cart/${orderId}/repeatOrder`, {
            method: 'POST',
            body: JSON.stringify({ replace: shouldReplace }),
            headers: {
                'X-CSRF-TOKEN': document
                    .querySelector('meta[name="csrf-token"]')
                    .getAttribute('content'),
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            await useCartFetchStore.getState().fetchCart();
            this.count = data.count;
            this.items = data.items;
            this.amount = data.amount;
            this.toggle();
        }
    },

    // Вызывается из QuantitySelector
    async updateQuantity(id, quantity) {
        const response = await fetch(`/cart/${id}/quantity`, {
            method: 'PATCH',
            body: JSON.stringify({ quantity }),
            headers: {
                'X-CSRF-TOKEN': document
                    .querySelector('meta[name="csrf-token"]')
                    .getAttribute('content'),
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            await useCartFetchStore.getState().fetchCart();
            this.count = data.count;
            this.items = data.items;
            this.amount = data.amount;
        }
    },
});

Alpine.store('favorites', {
    count: null,
    items: [],
    processing: [],

    async update() {
        const response = await fetch('/favorite/fetch');
        if (response.ok) {
            const data = await response.json();
            this.count = data.count;
            this.items = data.items;
        }
    },
    getItem(id) {
        return this.items.find((item) => item?.id === id);
    },
    isFavorite(id) {
        return !!this.getItem(id);
    },
    getItemByVariation(variationId) {
        return this.items.find(
            (item) => item?.product_variation_id === variationId,
        );
    },
    isFavoriteByVariation(variationId) {
        return !!this.getItemByVariation(variationId);
    },
    isProcessing(id) {
        return this.processing.includes(Number(id));
    },

    async toggle(id) {
        if (this.isProcessing(id)) return;

        this.processing.push(id);

        if (this.isFavoriteByVariation(id)) {
            await this.removeItem(this.getItemByVariation(id)?.id, id);
        } else {
            await this.addItem(id);
        }
    },
    async removeItem(id, variationId) {
        try {
            const response = await fetch(`/favorite/${id}/delete`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': document
                        .querySelector('meta[name="csrf-token"]')
                        .getAttribute('content'),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                this.count = data.count;
                this.items = data.items;
            }
        } catch (error) {
            console.error('Ошибка при переключении избранного:', error);
        } finally {
            this.processing = this.processing.filter(
                (variationId) => variationId !== variationId,
            );
        }
    },

    async addItem(variationId) {
        try {
            const response = await fetch(`/favorite/${variationId}/add`, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': document
                        .querySelector('meta[name="csrf-token"]')
                        .getAttribute('content'),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                this.count = data.count;
                this.items = data.items;
            }
        } catch (error) {
            console.error('Ошибка при переключении избранного:', error);
        } finally {
            this.processing = this.processing.filter(
                (variationId) => variationId !== variationId,
            );
        }
    },
});

Alpine.store('breakpoints', {
    width: window.innerWidth,
    screens: {
        mobile: 320,
        sm: 640, // 40rem
        md: 768, // 48rem
        lg: 1024, // 64rem
        xl: 1280, // 80rem
        '2xl': 1536, // 96rem
    },

    init() {
        window.addEventListener('resize', () => {
            this.width = window.innerWidth;
        });
    },
    is(key) {
        return this.width >= this.screens[key];
    },
});

Alpine.data('catalogInfiniteScroll', () => ({
    loading: false,
    error: false,
    hasMore: true,
    nextPageUrl: null,
    observer: null,

    init() {
        const nextLink = document.querySelector('a[rel="next"]');
        this.nextPageUrl = nextLink?.href || null;
        this.hasMore = !!this.nextPageUrl;

        if (this.hasMore) {
            this.setupObserver();
        }
    },

    setupObserver() {
        const sentinel = this.$refs.sentinel;

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.loading && this.hasMore) {
                        this.loadMore();
                    }
                });
            },
            {
                rootMargin: '200px',
            },
        );

        this.observer.observe(sentinel);
    },

    async loadMore() {
        if (!this.nextPageUrl || this.loading) return;

        this.loading = true;
        this.error = false;

        try {
            const response = await window.axios.get(this.nextPageUrl, {
                headers: { Accept: 'application/json' },
            });

            const grid = this.$refs.grid;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = response.data.html;

            Array.from(tempDiv.children).forEach((card) => {
                grid.appendChild(card);
            });

            this.nextPageUrl = response.data.next_page_url;
            this.hasMore = !!this.nextPageUrl;

            if (!this.hasMore && this.observer) {
                this.observer.disconnect();
            }
        } catch (e) {
            this.error = true;
            console.error('Failed to load more products:', e);
        } finally {
            this.loading = false;
        }
    },

    retry() {
        this.loadMore();
    },
}));

window.Alpine = Alpine;
Alpine.start();
